import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Check, ChevronDown, ChevronRight, ExternalLink, Layers, Package, Pencil, Plus, Search, ShoppingCart, Trash2, X,
} from 'lucide-react';
import { apiService } from '../../services/api';
import CadThumb from '../ProjectPage/CadThumb';
import ItemLinksCard from './ItemLinksCard';

// Catalogue du PLM, refait le 2026-10-12 (demande de Rémy : « plus pratique à utiliser et mieux intégré aux projets »).
// À gauche, chaque pièce avec sa vignette Fusion, sa référence, son prix et ce qui la relie (CAO, achats, stock, projets),
// filtrable. À droite, la fiche en onglets : aperçu, nomenclature chiffrée (et son édition par versions), utilisations
// et achats, modification. Les données de liens viennent de /plm/catalog et /plm/items/{id}/bom (plm_links.py).

const euros = (value) => (value == null ? '' : `${Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const STOCK_LABELS = { in_stock: 'en stock', ordered: 'commandé', at_client: 'chez le client', removed: 'retiré' };
const FILTERS = [
  ['toutes', 'Toutes'], ['cao', 'Liées à la CAO'], ['achetees', 'Achetées'], ['stock', 'En stock'], ['seules', 'Sans lien'],
];
const TABS = [['apercu', 'Aperçu'], ['nomenclature', 'Nomenclature'], ['utilisations', 'Utilisations et achats'], ['modifier', 'Modifier']];

const Badge = ({ tone, children, title }) => {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700', emerald: 'bg-emerald-50 text-emerald-700', amber: 'bg-amber-50 text-amber-700',
    slate: 'bg-slate-100 text-slate-600',
  };
  return <span title={title} className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tones[tone]}`}>{children}</span>;
};

const Thumb = ({ entry, className = 'h-11 w-11' }) => (entry?.design?.thumbnail ? (
  <CadThumb projectId={entry.design.project_id} itemId={entry.design.cad_item_id} version={entry.design.version} className={className} />
) : (
  <div className={`flex shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 text-sm font-semibold text-slate-400 ${className}`}>
    {(entry?.name || '?').slice(0, 1).toUpperCase()}
  </div>
));

const Tile = ({ label, children, hint }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <div className="mt-0.5 text-sm font-semibold text-slate-800">{children}</div>
    {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
  </div>
);

const stockTotal = (stock = {}) => (stock.in_stock || 0) + (stock.ordered || 0) + (stock.at_client || 0);

const BomTree = ({ nodes, depth = 0, onOpen }) => (
  <ul className={depth ? 'ml-4 border-l border-slate-200 pl-3' : ''}>
    {nodes.map((node) => (
      <li key={`${depth}-${node.item_id}`} className="py-1">
        <div className="flex items-center gap-2 text-sm">
          <button type="button" onClick={() => onOpen(node.item_id)} className="min-w-0 truncate text-left font-medium text-slate-800 hover:text-indigo-700">
            {node.name}
          </button>
          <span className="shrink-0 text-xs tabular-nums text-slate-500">×{node.qty}</span>
          <span className="ml-auto shrink-0 text-xs tabular-nums text-slate-600">
            {node.cost != null ? euros(node.cost) : <span className="text-amber-600">sans prix</span>}
          </span>
          {node.source && (
            <span className="shrink-0 text-[10px] text-slate-400">
              {{ prix: 'prix', achat: 'dernier achat', nomenclature: 'sa nomenclature' }[node.source]}
            </span>
          )}
        </div>
        {node.children?.length > 0 && <BomTree nodes={node.children} depth={depth + 1} onOpen={onOpen} />}
      </li>
    ))}
  </ul>
);

// L'édition de la nomenclature par versions, telle qu'elle existait : sous-pièces en brouillon, ajout, nouvelle version,
// historique.
const BomEditor = ({
  items, itemDetail, currentVersion, hasDraftChanges, resetDraftChanges, draftComponents, componentVersionMap,
  componentVersionLoading, handleDeleteComponent, updateDraftComponent, componentForm, handleComponentItemChange,
  setComponentForm, componentLoading, componentVersions, handleAddComponent, newVersion, setNewVersion,
  handleCreateVersion, renderCatalogComponents, catalogVersionComponents,
}) => (
  <div className="space-y-4">
    <div className="rounded-xl border border-slate-200/70 bg-slate-50 p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            {currentVersion ? `Version actuelle — V${currentVersion.version_number}${currentVersion.label ? ` · ${currentVersion.label}` : ''}` : 'Aucune version'}
          </div>
          {currentVersion?.description && <div className="text-xs text-slate-500">{currentVersion.description}</div>}
          {!currentVersion && <div className="text-xs text-slate-500">Ajoute des sous-pièces pour créer la première version.</div>}
        </div>
        {hasDraftChanges && (
          <button type="button" onClick={resetDraftChanges} className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-white">
            Annuler les changements
          </button>
        )}
      </div>
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-wide text-gray-400">Sous-pièces (brouillon de la prochaine version)</div>
        {draftComponents.length === 0 && <div className="text-xs text-slate-400">Aucune sous-pièce.</div>}
        {draftComponents.map((component) => {
          const versionKey = String(component.child_item_id || '');
          const versionOptions = componentVersionMap[versionKey] || [];
          const isLoadingVersions = componentVersionLoading[versionKey];
          return (
            <div key={component.id} className="rounded-lg border border-slate-200/70 bg-white p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold text-slate-800">{component.child_item_name || `Pièce #${component.child_item_id}`}</div>
                  <div className="text-[11px] text-slate-500">
                    {component.child_version_number ? `Version : V${component.child_version_number} ${component.child_version_label || ''}` : 'Dernière version'}
                  </div>
                </div>
                <button type="button" onClick={() => handleDeleteComponent(component.id)} className="text-xs text-rose-600 hover:text-rose-700">Retirer</button>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                <select
                  value={component.child_version_id || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    const nextVersion = versionOptions.find((version) => String(version.id) === String(value));
                    updateDraftComponent(component.id, {
                      child_version_id: value || '', child_version_number: nextVersion?.version_number || null,
                      child_version_label: nextVersion?.label || '',
                    });
                  }}
                  className="rounded border px-2 py-2 text-sm" disabled={isLoadingVersions}
                >
                  <option value="">{isLoadingVersions ? 'Chargement…' : 'Dernière version'}</option>
                  {versionOptions.map((versionOption) => (
                    <option key={versionOption.id} value={versionOption.id}>V{versionOption.version_number} {versionOption.label || ''}</option>
                  ))}
                </select>
                <input type="number" min="1" value={component.quantity} placeholder="Quantité"
                  onChange={(e) => updateDraftComponent(component.id, { quantity: e.target.value })} className="rounded border px-2 py-2 text-sm" />
                <input type="text" value={component.notes} placeholder="Note (optionnel)"
                  onChange={(e) => updateDraftComponent(component.id, { notes: e.target.value })} className="rounded border px-2 py-2 text-sm" />
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={handleAddComponent} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-wide text-gray-400">Ajouter une sous-pièce</div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <select value={componentForm.child_item_id} onChange={(e) => handleComponentItemChange(e.target.value)} className="rounded border px-2 py-2 text-sm" required>
            <option value="">Choisir une pièce</option>
            {items.filter((item) => item.id !== itemDetail.item.id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <select value={componentForm.child_version_id} onChange={(e) => setComponentForm((prev) => ({ ...prev, child_version_id: e.target.value }))}
            className="rounded border px-2 py-2 text-sm" disabled={!componentForm.child_item_id || componentLoading}>
            <option value="">{componentLoading ? 'Chargement…' : 'Version (optionnel)'}</option>
            {componentVersions.map((versionOption) => (
              <option key={versionOption.id} value={versionOption.id}>V{versionOption.version_number} {versionOption.label || ''}</option>
            ))}
          </select>
          <input type="number" min="1" value={componentForm.quantity} placeholder="Quantité"
            onChange={(e) => setComponentForm((prev) => ({ ...prev, quantity: e.target.value }))} className="rounded border px-2 py-2 text-sm" />
        </div>
        <textarea value={componentForm.notes} onChange={(e) => setComponentForm((prev) => ({ ...prev, notes: e.target.value }))}
          className="w-full rounded border px-2 py-2 text-sm" rows={2} placeholder="Note (optionnel)" />
        <button type="submit" className="rounded bg-blue-600 px-3 py-2 text-xs text-white hover:bg-blue-700">Ajouter</button>
      </form>
      <form onSubmit={handleCreateVersion} className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
        <div className="text-[10px] uppercase tracking-wide text-gray-400">Nouvelle version</div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <input type="text" placeholder="Libellé (ex. v1.2)" value={newVersion.label}
            onChange={(e) => setNewVersion((prev) => ({ ...prev, label: e.target.value }))} className="rounded border px-3 py-2 text-sm" />
          <input type="text" placeholder="Note de version" value={newVersion.description}
            onChange={(e) => setNewVersion((prev) => ({ ...prev, description: e.target.value }))} className="rounded border px-3 py-2 text-sm" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" disabled={!hasDraftChanges}
            className={`inline-flex items-center gap-2 rounded px-3 py-2 text-sm text-white ${hasDraftChanges ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-300'}`}>
            <Plus className="h-4 w-4" /> Enregistrer la nouvelle version
          </button>
          {!hasDraftChanges && <span className="text-[11px] text-gray-400">Aucun changement.</span>}
        </div>
      </form>
    </div>
    <div className="rounded-xl border border-slate-200/70 bg-white p-4 space-y-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-400">Historique des versions</div>
      {itemDetail.versions.length === 0 && <div className="text-xs text-slate-400">Aucune version.</div>}
      {itemDetail.versions.map((version) => (
        <div key={version.id} className="rounded-xl border border-slate-200/70 bg-slate-50 px-3 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-slate-900">V{version.version_number} {version.label ? `· ${version.label}` : ''}</div>
              {version.description && <div className="text-xs text-slate-500">{version.description}</div>}
            </div>
            {currentVersion?.id === version.id && <span className="rounded-full bg-blue-100 px-2 py-1 text-[10px] text-blue-700">Actuelle</span>}
          </div>
          <div className="mt-2 text-xs">
            {renderCatalogComponents(catalogVersionComponents[version.id] || version.components || [], 0, [version.id])}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const InventoryCatalogPanel = (props) => {
  const {
    items, loading, catalogItemId, setCatalogItemId, setShowNewItemModal, itemDetail, editingItem, setEditingItem,
    handleDeleteItem, handleUpdateItem, setActiveTab,
  } = props;
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('toutes');
  const [project, setProject] = useState('');
  const [tab, setTab] = useState('apercu');
  const [bom, setBom] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const versionsKey = (itemDetail?.versions || []).map((v) => v.id).join(',');

  useEffect(() => {
    apiService.get('/plm/catalog').then(setCatalog).catch(() => setCatalog(null));
  }, [items, itemDetail?.item?.updated_at, versionsKey]);

  useEffect(() => {
    if (!catalogItemId) { setBom(null); return; }
    apiService.get(`/plm/items/${catalogItemId}/bom`).then(setBom).catch(() => setBom(null));
  }, [catalogItemId, versionsKey]);

  const byId = useMemo(() => Object.fromEntries((catalog || []).map((c) => [c.id, c])), [catalog]);
  const projects = useMemo(() => {
    const seen = new Map();
    (catalog || []).forEach((c) => c.projects.forEach((p) => seen.set(p.id, p.name)));
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [catalog]);
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (catalog || items.map((i) => ({ ...i, projects: [], stock: {} }))).filter((c) => {
      if (needle && ![c.name, c.reference, c.supplier, c.description].some((v) => (v || '').toLowerCase().includes(needle))) return false;
      if (project && !c.projects.some((p) => String(p.id) === project)) return false;
      const cad = Boolean(c.design) || c.cad_parts > 0;
      if (filter === 'cao') return cad;
      if (filter === 'achetees') return c.purchases > 0;
      if (filter === 'stock') return (c.stock?.in_stock || 0) > 0;
      if (filter === 'seules') return !cad && !c.purchases && !c.projects.length && !stockTotal(c.stock);
      return true;
    });
  }, [catalog, items, search, filter, project]);

  const entry = byId[catalogItemId] || (itemDetail ? { ...itemDetail.item, projects: [], stock: {} } : null);
  const open = (id) => { setCatalogItemId(id); setTab('apercu'); };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
      <section className="space-y-3">
        <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-slate-800">Pièces <span className="font-normal text-slate-400">{rows.length}/{(catalog || items).length}</span></h2>
            <button type="button" onClick={() => setShowNewItemModal(true)}
              className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700">
              <Plus className="h-3.5 w-3.5" /> Nouvelle pièce
            </button>
          </div>
          <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 focus-within:border-indigo-300">
            <Search className="h-4 w-4 text-slate-400" />
            <input type="text" placeholder="Nom, référence, fournisseur…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
          </label>
          <div className="flex flex-wrap gap-1">
            {FILTERS.map(([key, label]) => (
              <button key={key} type="button" onClick={() => setFilter(key)}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${filter === key ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{label}</button>
            ))}
          </div>
          {projects.length > 0 && (
            <select value={project} onChange={(e) => setProject(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600">
              <option value="">Tous les projets</option>
              {projects.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
          <div className="max-h-[calc(100vh-22rem)] min-h-[16rem] space-y-1 overflow-auto pr-1">
            {loading.items && !catalog && <div className="text-sm text-gray-500">Chargement…</div>}
            {rows.map((c) => (
              <button key={c.id} type="button" onClick={() => open(c.id)}
                className={`flex w-full items-center gap-3 rounded-xl border px-2 py-2 text-left transition ${catalogItemId === c.id ? 'border-indigo-400 bg-indigo-50/60' : 'border-transparent hover:border-slate-200 hover:bg-slate-50'}`}>
                <Thumb entry={c} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-slate-800">{c.name}</span>
                    {c.unit_cost != null && <span className="ml-auto shrink-0 text-xs tabular-nums text-slate-500">{euros(c.unit_cost)}</span>}
                  </div>
                  <p className="truncate text-[11px] text-slate-500">{[c.reference, c.supplier].filter(Boolean).join(' · ') || c.description || '—'}</p>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {(c.design || c.cad_parts > 0) && <Badge tone="indigo" title="Liée à une conception Fusion"><Box className="h-3 w-3" />CAO</Badge>}
                    {c.purchases > 0 && <Badge tone="emerald"><ShoppingCart className="h-3 w-3" />{c.purchases}</Badge>}
                    {stockTotal(c.stock) > 0 && <Badge tone="amber"><Package className="h-3 w-3" />{c.stock.in_stock || 0} en stock</Badge>}
                    {c.projects.slice(0, 2).map((p) => <Badge key={p.id} tone="slate">{p.name}</Badge>)}
                  </div>
                </div>
              </button>
            ))}
            {catalog && rows.length === 0 && <div className="px-2 text-sm text-gray-500">Aucune pièce.</div>}
          </div>
        </div>
      </section>

      <section className="min-w-0 space-y-4">
        {!itemDetail || !entry ? (
          <div className="rounded-2xl border border-slate-200/70 bg-slate-50/70 p-6 text-sm text-slate-500">
            Choisis une pièce à gauche : sa fiche montre sa conception Fusion, sa nomenclature chiffrée, ses projets, ses achats et son stock.
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200/70 bg-white shadow-sm">
            <div className="flex flex-wrap items-start gap-4 border-b border-slate-100 p-5">
              <Thumb entry={entry} className="h-20 w-20" />
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-lg font-semibold text-slate-900">{itemDetail.item.name}</h2>
                <p className="text-sm text-slate-500">{[itemDetail.item.reference && `réf. ${itemDetail.item.reference}`, itemDetail.item.supplier].filter(Boolean).join(' · ') || 'Sans référence'}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {entry.projects.map((p) => (
                    <Link key={p.id} to={`/project/${p.id}?tab=materiel`} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-indigo-50 hover:text-indigo-700">{p.name}</Link>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {itemDetail.item.purchase_url && (
                  <a href={itemDetail.item.purchase_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800">
                    <ExternalLink className="h-3.5 w-3.5" /> Lien d&apos;achat
                  </a>
                )}
                <button type="button" onClick={handleDeleteItem} className="inline-flex items-center gap-1 text-sm text-rose-600 hover:text-rose-700">
                  <Trash2 className="h-4 w-4" /> Supprimer
                </button>
              </div>
            </div>
            <nav className="flex gap-1 overflow-x-auto border-b border-slate-100 px-4">
              {TABS.map(([key, label]) => (
                <button key={key} type="button" onClick={() => setTab(key)}
                  className={`shrink-0 border-b-2 px-3 py-2.5 text-sm font-semibold ${tab === key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>{label}</button>
              ))}
            </nav>
            <div className="space-y-4 p-5">
              {tab === 'apercu' && (
                <>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                    <Tile label="Prix unitaire" hint={itemDetail.item.unit_price == null && entry.last_purchase ? `dernier achat, ${entry.last_purchase.vendor}` : null}>
                      {entry.unit_cost != null ? euros(entry.unit_cost) : <span className="text-slate-400">—</span>}
                    </Tile>
                    <Tile label="Nomenclature" hint={bom?.missing ? `${bom.missing} ligne${bom.missing > 1 ? 's' : ''} sans prix` : null}>
                      {bom?.nodes?.length ? `${bom.nodes.length} sous-pièce${bom.nodes.length > 1 ? 's' : ''}${bom.total != null ? ` · ${euros(bom.total)}` : ''}` : <span className="text-slate-400">aucune</span>}
                    </Tile>
                    <Tile label="Stock" hint={Object.entries(entry.stock || {}).filter(([s]) => s !== 'in_stock' && s !== 'removed').map(([s, n]) => `${n} ${STOCK_LABELS[s] || s}`).join(' · ') || null}>
                      {(entry.stock?.in_stock || 0)} en stock
                    </Tile>
                    <Tile label="Conception Fusion" hint={entry.design ? `v${entry.design.version || '?'} · ${entry.design.name}` : null}>
                      {entry.design ? (entry.design.mass ? `${Number(entry.design.mass).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${entry.design.mass_unit || ''}` : 'liée') : <span className="text-slate-400">—</span>}
                    </Tile>
                  </div>
                  {itemDetail.item.description && <p className="whitespace-pre-wrap text-sm text-slate-600">{itemDetail.item.description}</p>}
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setTab('nomenclature')} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Layers className="h-3.5 w-3.5" /> Nomenclature</button>
                    <button type="button" onClick={() => setTab('utilisations')} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Box className="h-3.5 w-3.5" /> CAO et achats</button>
                    <button type="button" onClick={() => setActiveTab('stock')} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Package className="h-3.5 w-3.5" /> Stock</button>
                  </div>
                </>
              )}
              {tab === 'nomenclature' && (
                <>
                  {bom?.nodes?.length ? (
                    <div className="rounded-xl border border-slate-200 p-3">
                      <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                        <span>V{bom.version}{bom.label ? ` · ${bom.label}` : ''}</span>
                        <span className="font-semibold text-slate-800">
                          {bom.total != null ? `Total ${euros(bom.total)}` : 'Total inconnu'}{bom.missing ? ` (${bom.missing} sans prix)` : ''}
                        </span>
                      </div>
                      <BomTree nodes={bom.nodes} onOpen={open} />
                    </div>
                  ) : <p className="text-sm text-slate-400">Pas encore de sous-pièces. Ajoute-les ci-dessous, ou importe la nomenclature Fusion depuis l&apos;onglet PLM du projet.</p>}
                  <button type="button" onClick={() => setShowEditor((v) => !v)} className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800">
                    {showEditor ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} Modifier la nomenclature et les versions
                  </button>
                  {showEditor && <BomEditor {...props} />}
                </>
              )}
              {tab === 'utilisations' && <ItemLinksCard itemId={catalogItemId} />}
              {tab === 'modifier' && editingItem && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {[['name', 'Nom'], ['purchase_url', 'Lien d\'achat'], ['reference', 'Référence'], ['supplier', 'Fournisseur'], ['unit_price', 'Prix unitaire HT (€)']].map(([field, label]) => (
                      <label key={field} className="space-y-1">
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">{label}</span>
                        <input type={field === 'unit_price' ? 'number' : 'text'} step={field === 'unit_price' ? '0.01' : undefined}
                          value={editingItem[field] ?? ''} onChange={(e) => setEditingItem((prev) => ({ ...prev, [field]: e.target.value }))}
                          className="w-full rounded border px-3 py-2 text-sm" />
                      </label>
                    ))}
                  </div>
                  <label className="block space-y-1">
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">Description</span>
                    <textarea value={editingItem.description || ''} onChange={(e) => setEditingItem((prev) => ({ ...prev, description: e.target.value }))}
                      className="w-full rounded border px-3 py-2 text-sm" rows={3} />
                  </label>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={handleUpdateItem} className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm text-white">
                      <Check className="h-4 w-4" /> Enregistrer
                    </button>
                    <button type="button" onClick={() => setEditingItem(itemDetail.item)} className="inline-flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <X className="h-4 w-4" /> Annuler
                    </button>
                    <Pencil className="ml-auto h-4 w-4 text-slate-300" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default InventoryCatalogPanel;
